/**
 * Custom hook for building and assembling all columns for DocTypeListPageShell.
 * Includes: select, expand, thumbnail, resolved, system, add-column, and final merge.
 * Also creates the useReactTable instance with column sizing persistence.
 *
 * Extracted from DocTypeListPageShell.tsx for maintainability.
 */

import { useMemo, useCallback, useRef } from "react";
import type { ColumnDef, PaginationState, SortingState, ExpandedState, RowSelectionState } from "@tanstack/react-table";
import { useReactTable, getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { PlusIcon } from "lucide-react";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import type { useMetaDrivenListSettings } from "@/lib/hooks/useMetaDrivenListSettings";
import type { UserAvatarMap } from "../api/useUserAvatars";
import { applyListSettingsToColumns } from "./applyListSettingsToColumns";
import { createSystemColumns } from "./createSystemColumns";
import { createThumbnailColumn } from "./createThumbnailColumn";
import { createExpandCollapseColumn } from "./createExpandCollapseColumn";
import type { DocTypeListPageShellProps, NormalizedSystemConfig } from "./DocTypeListPageShell.types";

export type UseShellColumnsParams<T> = {
  // Column inputs
  columns: DocTypeListPageShellProps<T>['columns'];
  items: T[];
  visibleFieldOrderForBackend: string[];
  listSettings: ReturnType<typeof useMetaDrivenListSettings> | null;

  // Thumbnail
  thumbnailEnabled: boolean;
  thumbnailConfig?: DocTypeListPageShellProps<T>['thumbnailConfig'];
  detectedImageFieldnames: string[];
  thumbnailLabelFieldnames: string[];
  onRowClick?: (row: T) => void;

  // Expand
  getSubRows?: (row: T) => T[] | undefined;

  // System columns
  systemConfig: NormalizedSystemConfig | null;
  doctype: string;
  avatarMap: UserAvatarMap;

  // Prepend
  prependColumn?: ColumnDef<T, unknown>;
  prependColumns?: ColumnDef<T, unknown>[];

  // Table config
  pagination: PaginationState;
  sorting: SortingState;
  rowSelection: RowSelectionState;
  total: number;
  getRowId: (row: T) => string;
  effectiveExpanded: ExpandedState;
  effectiveOnExpandedChange: (updater: ExpandedState | ((prev: ExpandedState) => ExpandedState)) => void;
  onPaginationChange: (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => void;
  onSortingChange: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
  onRowSelectionChange: (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => void;
  setIsColumnSidebarOpen: (open: boolean) => void;
};

export function useShellColumns<T>(params: UseShellColumnsParams<T>) {
  const {
    columns, items, visibleFieldOrderForBackend, listSettings,
    thumbnailEnabled, thumbnailConfig, detectedImageFieldnames, thumbnailLabelFieldnames, onRowClick,
    getSubRows,
    systemConfig, doctype, avatarMap,
    prependColumn, prependColumns,
    pagination, sorting, rowSelection, total, getRowId,
    effectiveExpanded, effectiveOnExpandedChange,
    onPaginationChange, onSortingChange, onRowSelectionChange,
    setIsColumnSidebarOpen,
  } = params;

  // Build columns (support both static and dynamic from items)
  const resolvedColumns = useMemo(() => {
    let baseColumns: ColumnDef<T, unknown>[];

    if (typeof columns === "function") {
      baseColumns = columns({
        items,
        visibleFieldOrder: visibleFieldOrderForBackend,
        availableFields: listSettings?.availableFields,
      });
    } else {
      baseColumns = columns;
    }

    // CRITICAL: Apply list settings enforcement at shell level
    if (listSettings?.visibleFieldOrder && listSettings?.availableFields) {
      return applyListSettingsToColumns<T>(
        baseColumns,
        visibleFieldOrderForBackend,
        listSettings.availableFields
      );
    }

    return baseColumns;
  }, [columns, items, visibleFieldOrderForBackend, listSettings?.visibleFieldOrder, listSettings?.availableFields]);

  const thumbnailColumn = useMemo(() => {
    if (!thumbnailEnabled) return null;

    const sizePx = thumbnailConfig?.sizePx ?? 48;
    const imageFieldnames = detectedImageFieldnames;
    const labelFieldnames = thumbnailLabelFieldnames;

    return createThumbnailColumn<T>({
      sizePx,
      getImagePath: (row) => {
        const raw = row as unknown as Record<string, unknown>;
        for (const f of imageFieldnames) {
          const v = raw[f];
          if (typeof v === "string" && v.trim()) return v.trim();
        }
        return null;
      },
      getFallbackText: (row) => {
        const raw = row as unknown as Record<string, unknown>;
        for (const f of labelFieldnames) {
          const v = raw[f];
          if (typeof v === "string" && v.trim()) return v.trim();
        }
        const name = raw.name;
        return typeof name === "string" ? name : "";
      },
      onClick: onRowClick,
    });
  }, [
    thumbnailEnabled,
    thumbnailConfig?.sizePx,
    detectedImageFieldnames,
    thumbnailLabelFieldnames,
    onRowClick,
  ]);

  // Build expand/collapse column (far left, only if getSubRows is provided)
  const expandColumn = useMemo(() => {
    if (!getSubRows) return null;
    return createExpandCollapseColumn<T>({
      showForRow: ({ row, original }) => {
        const raw = original as unknown as Record<string, unknown>;
        const hasVariants = raw?.has_variants === 1 || raw?.has_variants === true;
        return row.depth === 0 && row.getCanExpand() && hasVariants;
      },
      widthPx: 40,
    });
  }, [getSubRows]);

  // Build system columns (non-configurable right-side strip)
  const systemColumns = useMemo(() => {
    if (!systemConfig) return [];

    return createSystemColumns<T>({
      doctype,
      assignedToField: systemConfig.assignedToField,
      commentCountField: systemConfig.commentCountField,
      likedByField: systemConfig.likedByField,
      createdField: systemConfig.createdField,
      showAssignedTo: systemConfig.showAssignedTo,
      showComments: systemConfig.showComments,
      showLikes: systemConfig.showLikes,
      showCreated: systemConfig.showCreated,
      avatarMap,
    });
  }, [systemConfig, doctype, avatarMap]);

  // Select column for row checkboxes
  const selectColumn: ColumnDef<T, unknown> = useMemo(() => ({
    id: '__select',
    header: ({ table: t }) => (
      <div className="flex items-center justify-center w-full">
        <Checkbox
          checked={
            t.getIsAllPageRowsSelected() ||
            (t.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => t.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex items-center justify-center w-full">
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 32,
    minSize: 32,
    maxSize: 32,
  }), []);

  // "Add column" trailing column (Attio-style) — fills remaining table width
  const addColumnDef: ColumnDef<T, unknown> = useMemo(() => ({
    id: '__add_column',
    header: () => (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsColumnSidebarOpen(true);
        }}
        className="flex items-center gap-1.5 px-3 w-full h-full text-muted-foreground/50 hover:text-muted-foreground transition-colors"
        aria-label="Add column"
      >
        <PlusIcon className="h-3.5 w-3.5" />
        <span className="text-xs font-normal normal-case tracking-normal">Add column</span>
      </button>
    ),
    cell: () => null,
    enableSorting: false,
    enableColumnFilter: false,
    enableResizing: false,
    size: 0,
    minSize: 0,
    maxSize: 9999,
  }), [setIsColumnSidebarOpen]);

  // Combine columns: select + expand (if enabled) + thumbnail (if enabled) + prepend columns + resolved columns + system columns + add column
  const finalColumns = useMemo(() => {
    const left: ColumnDef<T, unknown>[] = [selectColumn];
    if (expandColumn) left.push(expandColumn);
    if (thumbnailColumn) left.push(thumbnailColumn);
    if (prependColumns?.length) left.push(...prependColumns);
    if (prependColumn) left.push(prependColumn);

    const cols = [...left, ...resolvedColumns, ...systemColumns];
    if (listSettings) cols.push(addColumnDef);
    return cols;
  }, [selectColumn, expandColumn, thumbnailColumn, prependColumns, prependColumn, resolvedColumns, systemColumns, listSettings, addColumnDef]);

  // Column sizing — persisted per doctype
  const EMPTY_SIZING: Record<string, number> = useMemo(() => ({}), []);
  const storedColumnSizing = usePreferencesStore(
    useCallback((s) => s.docTypeColumnSizing[doctype] ?? EMPTY_SIZING, [doctype, EMPTY_SIZING])
  );
  const setStoredColumnSizing = usePreferencesStore((s) => s.setDocTypeColumnSizing);
  const columnSizing = storedColumnSizing;
  const columnSizingRef = useRef(storedColumnSizing);
  columnSizingRef.current = storedColumnSizing;
  const handleColumnSizingChange = useCallback(
    (updater: React.SetStateAction<Record<string, number>>) => {
      const next = typeof updater === 'function' ? updater(columnSizingRef.current) : updater;
      setStoredColumnSizing(doctype, next);
    },
    [doctype, setStoredColumnSizing]
  );

  // Column order — persisted per doctype (empty array = natural order).
  // Enables drag-to-reorder of data columns; pinned system columns ignore it.
  const EMPTY_ORDER: string[] = useMemo(() => [], []);
  const storedColumnOrder = usePreferencesStore(
    useCallback((s) => s.docTypeColumnOrder[doctype] ?? EMPTY_ORDER, [doctype, EMPTY_ORDER])
  );
  const setStoredColumnOrder = usePreferencesStore((s) => s.setDocTypeColumnOrder);
  const columnOrderRef = useRef(storedColumnOrder);
  columnOrderRef.current = storedColumnOrder;
  const handleColumnOrderChange = useCallback(
    (updater: React.SetStateAction<string[]>) => {
      const next = typeof updater === 'function' ? updater(columnOrderRef.current) : updater;
      setStoredColumnOrder(doctype, next);
    },
    [doctype, setStoredColumnOrder]
  );


  const table = useReactTable({
    data: items,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    ...(getSubRows ? { getSubRows, getExpandedRowModel: getExpandedRowModel() } : {}),
    getRowId,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    pageCount: total ? Math.ceil(total / pagination.pageSize) : -1,
    enableRowSelection: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    defaultColumn: {
      size: 150,
      minSize: 50,
      maxSize: 500,
    },
    state: {
      pagination,
      sorting,
      rowSelection,
      columnSizing,
      columnOrder: storedColumnOrder,
      ...(getSubRows ? { expanded: effectiveExpanded } : {}),
    },
    onPaginationChange,
    onSortingChange,
    onRowSelectionChange,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnOrderChange: handleColumnOrderChange,
    ...(getSubRows ? { onExpandedChange: effectiveOnExpandedChange } : {}),
  });

  return { table, finalColumns };
}
